import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import {
  LmoFracFctvsService,
  OverflowUpdate,
  OverflowVendor,
  OverflowVendorContract,
} from '~lmo/services/lmo-frac-fctvs.service';
import { LmoFracModalService } from '~lmo/services/lmo-frac-modal.service';
import { UserService } from '~services/user.service';
import { trackById } from '~utilities/trackById';

interface VendorControl {
  contract: OverflowVendorContract;
  fctvId: number;
  isOverflow: boolean;
  vendorId: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-frac-fctv-overflow',
  styleUrls: ['./lmo-frac-fctv-overflow.component.scss'],
  templateUrl: './lmo-frac-fctv-overflow.component.html',
})
export class LmoFracFctvOverflowComponent implements OnInit {
  public trackById = trackById;

  public vendorArray: FormArray;
  public overflowVendors;
  public vendorMap: { [key: string]: OverflowVendor };
  public overflowVendors$: Observable<OverflowVendor[]>;
  private currentOverflowVendorIds$: number[] = [];

  private destroy$$ = new Subject();

  constructor(
    private fctvService: LmoFracFctvsService,
    private cd: ChangeDetectorRef,
    private modalService: LmoFracModalService,
    public userService: UserService,
  ) {}

  public ngOnInit() {
    this.fctvService.currentFracOverflowVendors$
      .pipe(
        takeUntil(this.destroy$$),
        debounceTime(200),
        distinctUntilChanged((a, b) => {
          if (!a && !b) {
            return true;
          }
          if (a && !b) {
            return false;
          }
          if (b && !a) {
            return false;
          }
          return (a || []).length === (b || []).length;
        }),
        switchMap((vendors) => {
          this.overflowVendors$ = of(vendors as OverflowVendor[]);
          if (!vendors) {
            return of([] as OverflowVendor[]);
          }
          return of(vendors as OverflowVendor[]);
        }),
      )
      .subscribe((vendors) => {
        this.vendorMap = vendorIdArrayToRecord(vendors);
        this.vendorArray = new FormArray(vendors.map(overflowVendorToFormGroup).filter((formGroup) => !!formGroup));
        this.cd.markForCheck();
        this.vendorArray.controls.forEach((a) => {
          if (a.value.isOverflow) {
            this.currentOverflowVendorIds$.push(a.value.vendorId);
          }
        });
      });
  }

  public getVendorName(vendorControl: FormGroup): string {
    const value = vendorControl.value as VendorControl;
    const vendor = this.vendorMap[value.vendorId];
    return vendor ? vendor.vendorName : '';
  }

  public getContractChoices(vendorControl: FormGroup): OverflowVendorContract[] {
    const value = vendorControl.value as VendorControl;
    const vendor = this.vendorMap[value.vendorId];
    return vendor ? vendor.contracts : [];
  }

  public save() {
    const overflowVendors = (this.vendorArray.value as VendorControl[]).filter((vendor) => vendor.isOverflow);
    const nonOverflowVendors = (this.vendorArray.value as VendorControl[]).filter((vendor) => !vendor.isOverflow);
    const vendorControls = nonOverflowVendors.filter((item) => this.currentOverflowVendorIds$.includes(item.vendorId));
    const fctvIdsToRemove = vendorControls.map((vendor) => vendor.fctvId);
    const payload = overflowVendors.map(convertVendorControlToOverflowUpdate);
    this.fctvService.updateFctvOverflows$(payload, fctvIdsToRemove).subscribe((success) => {
      if (success) {
        this.modalService.closeHomeModal();
      }
    });
  }
}

function vendorIdArrayToRecord(arr: OverflowVendor[]): Record<string, OverflowVendor> {
  if (!arr) {
    return {};
  }
  return arr.reduce((record: Record<string, OverflowVendor>, o: OverflowVendor) => {
    record[o.vendorId] = o;
    return record;
  }, {});
}

function overflowVendorToFormGroup(vendor: OverflowVendor): FormGroup {
  if (!vendor.contracts || !vendor.contracts.length) {
    return null;
  }
  const vendorIdControl = new FormControl(vendor.vendorId);
  const isOverflowControl = new FormControl(vendor.contracts[0].isCurrentClusterOverflowVendor);
  const fctvIdControl = new FormControl(vendor.contracts[0].currentOverflowFCTVId);
  const contractControl = new FormControl(vendor.contracts[0]);
  contractControl.valueChanges.subscribe((contract: OverflowVendorContract) => {
    fctvIdControl.setValue(contract.currentOverflowFCTVId);
  });

  const group = new FormGroup({
    contract: contractControl,
    fctvId: fctvIdControl,
    isOverflow: isOverflowControl,
    vendorId: vendorIdControl,
  });
  return group;
}

function convertVendorControlToOverflowUpdate(vendorControl: VendorControl): OverflowUpdate {
  return {
    fctvId: vendorControl.fctvId,
    vendorContractId: vendorControl.contract.contractId,
  };
}
